import { useCallback, useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  SofaIcon,
  BedIcon,
  UtensilsIcon,
  BathIcon,
  ShirtIcon,
  AirVentIcon,
  DoorOpenIcon,
  LayoutGridIcon,
  ArrowLeftIcon } from
'lucide-react';
// Components
import { LoginScreen } from '../components/LoginScreen';
import { UnitSelectionScreen } from '../components/UnitSelectionScreen';
import { MobileHeader } from './components/MobileHeader';
import { SensorListView } from './components/SensorListView';
import { AlarmsSheet } from './components/AlarmsSheet';
import { SensorDetailSidebar } from './components/SensorDetailSidebar';
import { AllSpacesView } from './components/AllSpacesView';
import { Sensor, FloorPlanHeatMap } from './components/FloorPlanHeatMap';
import { CurtainController } from '../components/CurtainController';
// Services & Hooks
import { thingsboardService } from '../services/thingsboard';
import { useEditorZones } from '../hooks/useEditorZones';
import { useAlarms } from '../hooks/useAlarms';
import { useZonesTelemetry } from '../hooks/useZonesTelemetry';
import { ViewMode, VIEW_MODES } from '../utils/viewModeUtils';
import { useTranslation } from 'react-i18next';

// ─── Data ───
// ─── Constants ───
const ROOM_ICON_MAP: Record<string, any> = {
  '거실': SofaIcon,
  '침실': BedIcon,
  '침실1': BedIcon,
  '침실2': BedIcon,
  '안방': BedIcon,
  '주방': UtensilsIcon,
  '화장실': BathIcon,
  '화장실1': BathIcon,
  '화장실2': BathIcon,
  '드레스룸': ShirtIcon,
  '실외기실': AirVentIcon,
  '현관': DoorOpenIcon,
  '알파룸': LayoutGridIcon,
};

const DEFAULT_ICON = LayoutGridIcon;

// ─── View Types ───
type AppView = 'login' | 'unitSelect' | 'main' | 'sensorDetail' | 'floorPlan';
// Slide direction for transitions
type SlideDirection = 'left' | 'right' | 'up' | 'down' | null;
const slideVariants = {
  enterFromRight: {
    x: '100%',
    opacity: 0
  },
  enterFromLeft: {
    x: '-100%',
    opacity: 0
  },
  center: {
    x: 0,
    opacity: 1
  },
  exitToRight: {
    x: '100%',
    opacity: 0
  },
  exitToLeft: {
    x: '-100%',
    opacity: 0
  }
};

export function MobileApp() {
  const { t } = useTranslation();

  // ─── Theme Management ───
  type ThemePreference = 'dark' | 'light' | 'system';
  const [themePref, setThemePref] = useState<ThemePreference>(() => {
    return (localStorage.getItem('mobileThemePref') as ThemePreference) || 'system';
  });
  const [systemIsDark, setSystemIsDark] = useState<boolean>(
    () => window.matchMedia('(prefers-color-scheme: dark)').matches
  );

  // Listen to OS theme changes
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => setSystemIsDark(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const resolvedTheme: 'dark' | 'light' = useMemo(() => {
    if (themePref === 'system') return systemIsDark ? 'dark' : 'light';
    return themePref;
  }, [themePref, systemIsDark]);

  const toggleTheme = useCallback(() => {
    setThemePref((prev) => {
      const next = prev === 'system' ? 'light' : prev === 'light' ? 'dark' : 'system';
      localStorage.setItem('mobileThemePref', next);
      return next;
    });
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', resolvedTheme);
  }, [resolvedTheme]);

  // Auth
  const [authToken, setAuthToken] = useState<string | null>(localStorage.getItem('jwtToken'));
  
  const [apartmentAssetId, setApartmentAssetId] = useState<string | null>(localStorage.getItem('apartmentAssetId'));
  const [apartmentLabel, setApartmentLabel] = useState<string | null>(localStorage.getItem('apartmentLabel'));

  // Navigation
  const [currentView, setCurrentView] = useState<AppView>(() => {
    if (localStorage.getItem('jwtToken')) {
      return localStorage.getItem('apartmentAssetId') ? 'main' : 'unitSelect';
    }
    return 'login';
  });
  const [prevView, setPrevView] = useState<AppView>('main');
  const [slideDirection, setSlideDirection] = useState<SlideDirection>('left');

  // UI State
  const [selectedRoomId, setSelectedRoomId] = useState<string | null>('ALL');
  const [selectedSensor, setSelectedSensor] = useState<Sensor | null>(null);
  const [showAlarms, setShowAlarms] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('energy');
  const [isMapZoomed, setIsMapZoomed] = useState(false);
  const [floorPlanUrl, setFloorPlanUrl] = useState<string | null>(null);
  const [seniorCareEnabled, setSeniorCareEnabled] = useState(false);
  const [absenceAlarmEnabled, setAbsenceAlarmEnabled] = useState(false);
  const [deviceToast, setDeviceToast] = useState<{ name: string; alias?: string } | null>(null);

  // Real-time Data Hooks
  const { messages: alarmMessages } = useAlarms(apartmentAssetId);
  const { zones } = useEditorZones(apartmentAssetId);
  const zoneIds = zones.map(z => z.id);
  const { zonesData: assetTelemetry } = useZonesTelemetry(zoneIds, ['temperature', 'zone_total_power', 'calculated_24h_power', 'humidity', 'CO2', 'TVOCs', 'dust', 'presence_state', 'light_state']);

  // Get all sensor IDs for the selected room (default to first room if none selected)
  const currentZone = zones.find(z => z.id === selectedRoomId) || zones[0];
  const currentRoomSensors = currentZone?.sensors || [];
  
  // Memoize sensor IDs to keep telemetry subscription stable
  const sensorIds = useMemo(() => currentRoomSensors.map(s => s.id), [currentRoomSensors]);
  const { zonesData: sensorTelemetry } = useZonesTelemetry(sensorIds, ['temperature', 'power_instant', 'humidity', 'CO2', 'TVOCs', 'dust', 'presence_state', 'light_state', 'curtain_state'], 'DEVICE');

  // Flatten all sensors for CurtainController lookup
  const allSensors = useMemo(() => {
    return zones.flatMap(z => z.sensors);
  }, [zones]);

  // Listen for device events
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      setDeviceToast({ name: detail.name, alias: detail.alias });
    };
    window.addEventListener('device-toast', handler);
    return () => window.removeEventListener('device-toast', handler);
  }, []);

  // Derive all SEO sensors for the whole apartment
  const seoSensors = useMemo(() => {
    return zones.flatMap(z => 
      z.sensors
        .filter(s => s.type === 'SEO' || s.type === 'SEM' || s.type === 'SER' || s.type === 'PLUG' || s.type === 'OUTLET')
        .map(s => ({ ...s, roomId: z.id, roomName: z.name }))
    );
  }, [zones]);


  // Fetch dynamic floorplan URL and Senior Care status
  useEffect(() => {
    async function fetchApartmentData() {
      if (!apartmentAssetId || apartmentAssetId === '00000000-0000-0000-0000-000000000000') {
        setFloorPlanUrl(null);
        return;
      }
      try {
        const attrs = await thingsboardService.getEntityAttributes('ASSET', apartmentAssetId, 'SERVER_SCOPE', ['floorplan','homeName', 'senior_care_enabled', 'absence_alarm_enabled']);
        if (attrs && attrs.length > 0) {
          const fpAttr = attrs.find((a: any) => a.key === 'floorplan');
          if (fpAttr && fpAttr.value) {
            setFloorPlanUrl(`https://iot.seqsys.net${fpAttr.value}`);
          }
          const seniorAttr = attrs.find((a: any) => a.key === 'senior_care_enabled');
          if (seniorAttr) setSeniorCareEnabled(String(seniorAttr.value) === 'true');
          
          const absenceAttr = attrs.find((a: any) => a.key === 'absence_alarm_enabled');
          if (absenceAttr) setAbsenceAlarmEnabled(String(absenceAttr.value) === 'true');
        }
      } catch (err) {
        console.error('Failed to fetch apartment attributes:', err);
      }
    }
    fetchApartmentData();
  }, [apartmentAssetId]);

  useEffect(() => {
    if (zones.length > 0 && !selectedRoomId) {
      setSelectedRoomId(zones[0].id);
    }
  }, [zones, selectedRoomId]);

  // ─── Login & Asset Selection Flow ───
  const handleLogin = (jwtToken: string) => {
    setAuthToken(jwtToken);
    localStorage.setItem('jwtToken', jwtToken);
    setSlideDirection('left');
    setCurrentView('unitSelect');
  };

  const handleUnitSelect = (assetId: string, assetName: string) => {
    setApartmentAssetId(assetId);
    setApartmentLabel(assetName);
    localStorage.setItem('apartmentAssetId', assetId);
    localStorage.setItem('apartmentLabel', assetName);
    setSlideDirection('left');
    setCurrentView('main');
  };

  const handleLogout = () => {
    thingsboardService.logout();
    setAuthToken(null);
    setApartmentAssetId(null);
    setApartmentLabel(null);
    setCurrentView('login');
  };

  const handleBackToUnits = () => {
    setSlideDirection('right');
    setCurrentView('unitSelect');
  };

  const ROOMS = useMemo(() => {
    const baseRooms = zones.map(z => {
      const data = assetTelemetry[z.id] || {};
      const temp = Number(data.temperature?.[0]?.value || 0);
      const humidity = Number(data.humidity?.[0]?.value || 0);
      const energy = Number(data.calculated_24h_power?.[0]?.value || 0);
      const co2 = Number(data.CO2?.[0]?.value || 0);
      const tvoc = Number(data.TVOCs?.[0]?.value || 0);
      const dust = Number(data.dust?.[0]?.value || 0);
      
      // Simple AQI calculation
      const aqi = Math.round((
        (co2 < 800 ? 100 : co2 < 1200 ? 70 : 40) +
        (tvoc < 0.3 ? 100 : tvoc < 0.6 ? 70 : 40) +
        (dust < 15 ? 100 : dust < 35 ? 70 : 40)
      ) / 3);

      return {
        id: z.id,
        name: z.name,
        temp,
        humidity,
        energy,
        co2,
        tvoc,
        dust,
        aqi,
        icon: ROOM_ICON_MAP[z.name] || DEFAULT_ICON,
        sensors: z.sensors
      };
    });
    
    // Prefix '공간전체' as the first option
    return [
      { id: 'ALL', name: '공간전체', temp: 0, icon: LayoutGridIcon, sensors: [] },
      ...baseRooms
    ];
  }, [zones, assetTelemetry]);

  const currentRoom = useMemo(() => ROOMS.find((r) => r.id === selectedRoomId) || ROOMS[0], [ROOMS, selectedRoomId]);
  const currentSensors = useMemo(() => currentRoom?.sensors || [], [currentRoom]);

  const handleToggleSeniorCare = async () => {
    const newState = !seniorCareEnabled;
    setSeniorCareEnabled(newState);
    const newAbsenceState = newState ? absenceAlarmEnabled : false;
    if (!newState && absenceAlarmEnabled) {
      setAbsenceAlarmEnabled(false);
    }
    
    if (!apartmentAssetId) return;
    try {
      await thingsboardService.saveEntityAttributes('ASSET', apartmentAssetId, 'SERVER_SCOPE', {
        senior_care_enabled: newState,
        absence_alarm_enabled: newAbsenceState
      });
    } catch (err) {
      console.error('Failed to save senior care state:', err);
      setSeniorCareEnabled(!newState);
    }
  };

  const handleToggleAbsenceAlarm = async () => {
    if (!seniorCareEnabled || !apartmentAssetId) return;
    const newState = !absenceAlarmEnabled;
    setAbsenceAlarmEnabled(newState);
    try {
      await thingsboardService.saveEntityAttributes('ASSET', apartmentAssetId, 'SERVER_SCOPE', {
        absence_alarm_enabled: newState
      });
    } catch (err) {
      console.error('Failed to save absence alarm state:', err);
      setAbsenceAlarmEnabled(!newState);
    }
  };

  // ─── Handlers ───
  const handleSelectRoom = useCallback((id: string) => {
    setSelectedRoomId(id);
  }, []);

  const handleSelectSensor = useCallback((sensor: Sensor) => {
    // If it's a curtain or blind type, show the controller instead of the detail page
    if (sensor.type === 'SEC' || sensor.type === 'CURTAIN' || sensor.type === 'BLIND') {
      window.dispatchEvent(
        new CustomEvent('device-toast', { detail: { name: sensor.label, alias: sensor.alias } })
      );
      return;
    }

    setPrevView(currentView);
    setSelectedSensor(sensor);
    setSlideDirection('left');
    setCurrentView('sensorDetail');
  }, [currentView]);

  const handleBackFromSensor = useCallback(() => {
    setSlideDirection('right');
    setCurrentView(prevView);
    setSelectedSensor(null);
  }, [prevView]);

  const handleOpenMap = useCallback(() => {
    setSlideDirection('left');
    setIsMapZoomed(false);
    setCurrentView('floorPlan');
  }, []);

  const handleCloseMap = useCallback(() => {
    setSlideDirection('right');
    setCurrentView('main');
  }, []);

  // 1. Loading Splash
  if (!authToken) {
    // Show login if no token
  }


  // 2. Login
  if (!authToken) {
    return <LoginScreen onLogin={handleLogin} />;
  }

  // 3. Unit Selection
  if (currentView === 'unitSelect' || !apartmentAssetId) {
    return (
      <UnitSelectionScreen 
        isMobile 
        onSelect={handleUnitSelect} 
        onLogout={handleLogout} 
      />
    );
  }

  return (
    <div
      className="h-[100dvh] flex flex-col overflow-hidden max-w-md mx-auto relative font-sans"
      data-theme={resolvedTheme}
      style={{ backgroundColor: 'var(--mobile-bg)', color: 'var(--mobile-text-primary)' }}
    >
      {/* Background Decor - hidden in light mode via CSS */}
      <div
        className="fixed inset-0 pointer-events-none z-0 opacity-10 mobile-grid-decor"
        style={{
          backgroundImage:
          'linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)',
          backgroundSize: '32px 32px'
        }} />
      {resolvedTheme === 'dark' && (
        <div className="fixed inset-0 pointer-events-none z-0 bg-[radial-gradient(circle_at_center,_transparent_0%,_#0a0f1a_100%)]" />
      )}

      {/* Header Section */}
      <div className="relative z-20 flex-shrink-0">
        {currentView === 'sensorDetail' && selectedSensor ? (
          <div
            className="h-14 flex items-center px-4 backdrop-blur-md border-b"
            style={{ backgroundColor: 'var(--mobile-bg-header)', borderColor: 'var(--mobile-border)' }}
          >
            <button onClick={handleBackFromSensor} aria-label="뒤로" className="p-2 -ml-1 rounded-lg transition-colors" style={{ color: 'var(--mobile-text-muted)' }}>
              <ArrowLeftIcon className="w-5 h-5" />
            </button>
            <div className="flex-1 text-center font-medium font-bold" style={{ color: 'var(--mobile-text-primary)' }}>{t('app.sed_info')}</div>
            <div className="w-9" />
          </div>
        ) : currentView === 'floorPlan' ? (
          <div
            className="h-14 flex items-center px-4 backdrop-blur-md border-b"
            style={{ backgroundColor: 'var(--mobile-bg-header)', borderColor: 'var(--mobile-border)' }}
          >
            <button onClick={handleCloseMap} aria-label="뒤로" className="p-2 -ml-1 rounded-lg transition-colors" style={{ color: 'var(--mobile-text-muted)' }}>
              <ArrowLeftIcon className="w-5 h-5" />
            </button>
            <div className="flex-1 text-center font-medium font-bold" style={{ color: 'var(--mobile-text-primary)' }}>{t('app.floorplan')}</div>
            <div className="w-9" />
          </div>
        ) : (
          <MobileHeader
            rooms={ROOMS as any}
            selectedRoomId={selectedRoomId || ''}
            onSelectRoom={handleSelectRoom}
            onOpenMap={handleOpenMap}
            onOpenAlarms={() => setShowAlarms(true)}
            hasAlarms={alarmMessages.length > 0}
            onBack={() => handleBackToUnits()}
            apartmentLabel={apartmentLabel || ''}
            currentRoomName={currentRoom?.name || ''}
            sensorsCount={currentSensors.length}
            themePref={themePref}
            onThemeToggle={toggleTheme}
          />
        )}
      </div>

      {/* Main Container */}
      <div className="flex-1 flex flex-col relative z-10 overflow-hidden">
        
        {/* Dynamic Content Views */}
        <div className="flex-1 relative overflow-hidden">
        <AnimatePresence initial={false}>
          {currentView === 'main' && (
            <motion.div
              key="main"
              variants={slideVariants}
              initial={slideDirection === 'left' ? 'enterFromRight' : slideDirection === 'right' ? 'enterFromLeft' : undefined}
              animate="center"
              exit={slideDirection === 'right' ? 'exitToRight' : slideDirection === 'left' ? 'exitToLeft' : undefined}
              transition={{ type: 'tween', duration: 0.25, ease: 'easeInOut' }}
              className="absolute inset-0 flex flex-col"
              style={{ backgroundColor: 'var(--mobile-bg-alt)' }}
            >
              {selectedRoomId === 'ALL' ? (
                <AllSpacesView 
                  apartmentAssetId={apartmentAssetId} 
                  homeName={apartmentLabel} 
                  seoSensors={seoSensors}
                />
              ) : (
                <SensorListView
                  room={currentRoom as any}
                  telemetry={sensorTelemetry}
                  onSelectSensor={handleSelectSensor}
                  sensors={currentSensors}
                />
              )}
            </motion.div>
          )}

            {currentView === 'sensorDetail' && selectedSensor && (
              <motion.div
                key="sensorDetail"
                variants={slideVariants}
                initial="enterFromRight"
                animate="center"
                exit="exitToRight"
                transition={{ type: 'tween', duration: 0.25, ease: 'easeInOut' }}
                className="absolute inset-0 overflow-y-auto"
              >
                <SensorDetailSidebar
                  sensor={selectedSensor}
                  onBack={handleBackFromSensor}
                  onClose={handleBackFromSensor}
                />
              </motion.div>
            )}

            {currentView === 'floorPlan' && (
              <motion.div
                key="floorPlan"
                variants={slideVariants}
                initial="enterFromRight"
                animate="center"
                exit="exitToRight"
                transition={{ type: 'tween', duration: 0.25, ease: 'easeInOut' }}
                className="absolute inset-0 flex flex-col"
              >
                {/* View Mode Tabs */}
                <div className="px-5 mb-4 mt-2 flex-shrink-0">
                  <div className="flex backdrop-blur-md rounded-2xl p-1 border shadow-inner"
                    style={{ background: 'var(--mobile-bg-card)', borderColor: 'var(--mobile-border)' }}>
                    {VIEW_MODES.map((mode) => {
                      const Icon = mode.icon;
                      const isActive = viewMode === mode.id || 
                                     (viewMode === 'aqi' && mode.id === 'airquality') || 
                                     (viewMode === 'airquality' && mode.id === 'aqi');
                      return (
                        <button
                          key={mode.id}
                          onClick={() => setViewMode(mode.id)}
                          className={`flex-1 flex flex-col items-center justify-center py-2.5 rounded-xl transition-all duration-300 relative`}
                          style={isActive
                            ? { background: 'var(--mobile-tab-active)', color: 'var(--mobile-text-primary)' }
                            : { color: 'var(--mobile-text-muted)' }}
                        >
                          <Icon className={`w-5 h-5 mb-1 ${isActive ? mode.activeColor : 'text-[var(--mobile-text-muted)]'}`} />
                          <span className={`text-[0.8rem] font-medium font-bold uppercase tracking-wider ${isActive ? mode.activeColor : 'text-[var(--mobile-text-muted)]'}`}>
                            {t(mode.label)}
                          </span>
                          {isActive && (
                            <motion.div
                              layoutId="activeTab"
                              className="absolute -bottom-1 w-1 h-1 rounded-full bg-cyan-400"
                            />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="flex-1 flex items-center justify-center p-2 overflow-hidden">
                  <FloorPlanHeatMap
                    rooms={ROOMS as any}
                    selectedRoomId={selectedRoomId || ''}
                    onSelectRoom={(id) => {
                      handleSelectRoom(id);
                      setIsMapZoomed(true);
                    }}
                    isZoomed={isMapZoomed}
                    onZoomOut={() => {
                      setIsMapZoomed(false);
                      setSelectedRoomId('ALL');
                    }}
                    onSelectSensor={handleSelectSensor}
                    viewMode={viewMode}
                    seniorCareEnabled={seniorCareEnabled}
                    onToggleSeniorCare={handleToggleSeniorCare}
                    absenceAlarmEnabled={absenceAlarmEnabled}
                    onToggleAbsenceAlarm={handleToggleAbsenceAlarm}
                    zoneOverrides={zones.map((z) => ({
                      id: z.id,
                      name: z.name,
                      top: z.top,
                      left: z.left,
                      width: z.width,
                      height: z.height,
                      points: z.points,
                      sensors: z.sensors
                    }))}
                    floorPlanUrl={floorPlanUrl}
                    currentAssetId={apartmentAssetId}
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Alarms Overlay */}
      <AnimatePresence>
        {showAlarms && (
          <AlarmsSheet 
            onClose={() => setShowAlarms(false)} 
            apartmentAssetId={apartmentAssetId}
          />
        )}
      </AnimatePresence>

      {/* Curtain/Blind Controller */}
      <CurtainController
        isOpen={!!deviceToast && deviceToast.name.startsWith('SEC_')}
        deviceName={deviceToast?.name || null}
        alias={deviceToast?.alias || null}
        onClose={() => setDeviceToast(null)}
        sensors={allSensors}
      />

      {/* Device Message Dialog (Future Connection) */}
      <AnimatePresence>
        {deviceToast && !deviceToast.name.startsWith('SEC_') &&
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[300] flex items-center justify-center p-4"
          onClick={() => setDeviceToast(null)}>
          
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
            <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="relative z-10 w-full max-w-xs bg-slate-800/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}>
            
              <div className="flex items-center gap-3 px-5 py-4 border-b border-white/5">
                <div className="p-2 rounded-lg bg-cyan-500/20 text-cyan-400">
                  <LayoutGridIcon className="w-5 h-5" />
                </div>
                <div className="text-base font-bold text-white">
                  {deviceToast.alias}-{deviceToast.name}
                </div>
              </div>

              <div className="px-5 py-4">
                <p className="text-sm text-slate-300">{t('app.futureConnection')}</p>
              </div>
              <div className="px-5 pb-4">
                <button
                  onClick={() => setDeviceToast(null)}
                  className="w-full py-2.5 rounded-xl bg-cyan-500/20 text-cyan-400 text-sm font-bold active:scale-95 transition-all"
                >
                  {t('common.confirm')}
                </button>
              </div>
            </motion.div>
          </motion.div>
        }
      </AnimatePresence>
    </div>
  );
}
